/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

// Custom Pagination component
import Pagination from "components/common/pagination/Pagination";

// Context
import { useMaterialUIController } from "context";

// Data
import registroActividadesData from "./data";

const ITEMS_PER_PAGE = 5;

function RegistroActividades() {
  const { columns, rows: initialRows } = registroActividadesData();
  const [controller] = useMaterialUIController();
  const { searchTerm } = controller;

  const [filterDate, setFilterDate] = useState({
    start: "",
    end: "",
  });
  const [filterPlan, setFilterPlan] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filteredRows, setFilteredRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    color: "success",
  });

  const showNotification = (message, color = "success") => {
    setNotification({
      open: true,
      message,
      color,
    });
  };

  const closeNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  // 1. Filter by global search term first
  const searchedRows = useMemo(() => {
    if (!searchTerm) {
      return initialRows;
    }
    const lowercasedSearchTerm = searchTerm.toLowerCase();
    return initialRows.filter((row) => {
      const { empresa, plan_contratado, estado, vendedor } = row.rawData || {};
      return (
        (empresa && empresa.toLowerCase().includes(lowercasedSearchTerm)) ||
        (plan_contratado && plan_contratado.toLowerCase().includes(lowercasedSearchTerm)) ||
        (estado && estado.toLowerCase().includes(lowercasedSearchTerm)) ||
        (vendedor && vendedor.toLowerCase().includes(lowercasedSearchTerm))
      );
    });
  }, [initialRows, searchTerm]);

  // Aplicar filtros automáticamente cuando cambien
  useEffect(() => {
    let filtered = [...searchedRows];

    // Filtro por fecha de inicio
    if (filterDate.start) {
      filtered = filtered.filter((row) => {
        const fechaInicio = row.rawData?.fecha_inicio;
        return fechaInicio && fechaInicio >= filterDate.start;
      });
    }

    // Filtro por fecha de fin
    if (filterDate.end) {
      filtered = filtered.filter((row) => {
        const fechaInicio = row.rawData?.fecha_inicio;
        return fechaInicio && fechaInicio <= filterDate.end;
      });
    }

    // Filtro por plan
    if (filterPlan) {
      filtered = filtered.filter((row) => {
        return row.rawData?.plan_contratado === filterPlan;
      });
    }

    // Filtro por estado
    if (filterStatus) {
      filtered = filtered.filter((row) => {
        return row.rawData?.estado === filterStatus;
      });
    }

    setFilteredRows(filtered);
  }, [filterDate.start, filterDate.end, filterPlan, filterStatus, searchedRows]);

  // Reset page to 1 when search term changes, which affects searchedRows
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const applyFilters = () => {
    // La lógica de applyFilters ahora está dentro de useEffect,
    // este handler es principalmente para mostrar notificaciones.
    const totalResults = filteredRows.length;
    const filtersActive =
      [filterDate.start, filterDate.end, filterPlan, filterStatus].filter(Boolean).length > 0;

    if (filtersActive) {
      showNotification(`Filtros aplicados: ${totalResults} resultado(s) encontrado(s)`, "info");
    } else {
      showNotification("Mostrando todos los resultados", "info");
    }
  };

  const handleDateChange = (field, value) => {
    setFilterDate(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFilter = () => {
    applyFilters();
  };

  const handleClearFilters = () => {
    setFilterDate({ start: "", end: "" });
    setFilterPlan("");
    setFilterStatus("");
    // El useEffect se encargará de actualizar las filas
    showNotification("Filtros limpiados - Mostrando todos los resultados", "warning");
  };

  const handleDownloadPDF = () => {
    // Implementar descarga PDF
    showNotification("Descargando PDF...", "info");
    console.log("Descargando PDF");
  };

  const handleDownloadCSV = () => {
    // Implementar descarga CSV
    showNotification("Descargando CSV...", "info");
    console.log("Descargando CSV");
  };

  // Pagination logic
  const totalItems = filteredRows.length;
  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>

              
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Registro de Actividades
                </MDTypography>
              </MDBox>

              {/* Área de filtros */}
              <MDBox pt={3} px={3}>
                <Card sx={{ p: 3, mb: 3, border: "1px solid #e0e0e0" }}>
                  <Grid container spacing={3} alignItems="flex-end">
                    {/* Filtros de fecha */}
                    <Grid item xs={12} md={3}>
                      <MDTypography variant="body2" fontWeight="bold" mb={1}>
                        Rango de fechas
                      </MDTypography>
                      <MDBox display="flex" flexDirection="column" gap={1}>
                        <TextField
                          type="date"
                          label="Fecha inicio"
                          value={filterDate.start}
                          onChange={(e) => handleDateChange("start", e.target.value)}
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          sx={{ 
                            '& .MuiOutlinedInput-root': { 
                              height: '56px',
                              '& input': {
                                padding: '16.5px 14px'
                              }
                            } 
                          }}
                        />
                        <TextField
                          type="date"
                          label="Fecha fin"
                          value={filterDate.end}
                          onChange={(e) => handleDateChange("end", e.target.value)}
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          sx={{ 
                            '& .MuiOutlinedInput-root': { 
                              height: '56px',
                              '& input': {
                                padding: '16.5px 14px'
                              }
                            } 
                          }}
                        />
                      </MDBox>
                    </Grid>

                    {/* Filtro de plan */}
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        select
                        label="Plan"
                        value={filterPlan}
                        onChange={(e) => setFilterPlan(e.target.value)}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                          displayEmpty: true,
                        }}
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            height: '56px',
                            '& .MuiSelect-select': {
                              padding: '16.5px 14px',
                              display: 'flex',
                              alignItems: 'center'
                            }
                          } 
                        }}
                      >
                        <MenuItem value="">
                          <em>Todos</em>
                        </MenuItem>
                        <MenuItem value="Avanzado">Avanzado</MenuItem>
                        <MenuItem value="Medio">Medio</MenuItem>
                        <MenuItem value="Básico">Básico</MenuItem>
                      </TextField>
                    </Grid>

                    {/* Filtro de estado */}
                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        select
                        label="Estado"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        variant="outlined"
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                          displayEmpty: true,
                        }}
                        sx={{ 
                          '& .MuiOutlinedInput-root': { 
                            height: '56px',
                            '& .MuiSelect-select': {
                              padding: '16.5px 14px',
                              display: 'flex',
                              alignItems: 'center'
                            }
                          } 
                        }}
                      >
                        <MenuItem value="">
                          <em>Todos</em>
                        </MenuItem>
                        <MenuItem value="Convertida">Convertida</MenuItem>
                        <MenuItem value="No convertida">No convertida</MenuItem>
                        <MenuItem value="Completado">Completado</MenuItem>
                      </TextField>
                    </Grid>

                    {/* Botones de acción */}
                    <Grid item xs={12} md={5}>
                      <MDBox display="flex" gap={2} justifyContent="flex-end" flexWrap="wrap">
                        <MDButton
                          variant="gradient"
                          color="warning"
                          size="small"
                          onClick={handleFilter}
                        >
                          <Icon sx={{ mr: 1 }}>filter_list</Icon>
                          Filtrar
                        </MDButton>
                        
                        <MDButton
                          variant="outlined"
                          color="secondary"
                          size="small"
                          onClick={handleClearFilters}
                        >
                          <Icon sx={{ mr: 1 }}>clear</Icon>
                          Limpiar
                        </MDButton>

                        <MDButton
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={handleDownloadPDF}
                        >
                          <Icon sx={{ mr: 1 }}>picture_as_pdf</Icon>
                          PDF
                        </MDButton>

                        <MDButton
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={handleDownloadCSV}
                        >
                          <Icon sx={{ mr: 1 }}>table_view</Icon>
                          CSV
                        </MDButton>
                      </MDBox>
                    </Grid>
                  </Grid>
                </Card>
              </MDBox>

              {/* Contador de resultados */}
              <MDBox px={3} pb={2}>
                <MDTypography variant="body2" color="text">
                  Mostrando <strong>{filteredRows.length}</strong> de <strong>{initialRows.length}</strong> actividades
                  {(filterDate.start || filterDate.end || filterPlan || filterStatus) && (
                    <span> (filtrado)</span>
                  )}
                </MDTypography>
              </MDBox>

              {/* Tabla de actividades */}
              <MDBox pt={1} pb={3}>
                {filteredRows.length > 0 ? (
                  <DataTable
                    table={{ columns, rows: paginatedRows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                ) : (
                  <MDBox p={3} textAlign="center">
                    <MDTypography variant="h6" color="text">
                      No se encontraron resultados
                    </MDTypography>
                    <MDTypography variant="body2" color="text" mt={1}>
                      Intenta ajustar los filtros para obtener más resultados
                    </MDTypography>
                  </MDBox>
                )}
                <Pagination
                  itemsPerPage={ITEMS_PER_PAGE}
                  totalItems={totalItems}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      
      <Footer />

      {/* Notificaciones */}
      <MDSnackbar
        color={notification.color}
        icon={notification.color === "success" ? "check" : notification.color === "error" ? "warning" : "info"}
        title={notification.color === "success" ? "Éxito" : notification.color === "error" ? "Error" : "Información"}
        content={notification.message}
        dateTime=""
        open={notification.open}
        onClose={closeNotification}
        close={closeNotification}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default RegistroActividades; 